import Modal from "common/Modal/Modal";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { SECURITY_SETTINGS_PATH } from "common/constants";
import EmptyView from "common/Empty/EmptyView.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import { EmptyStatesType } from "common/Icons/types.ts";
import Button from "common/Buttons/Button.tsx";
import { LanguageNameSpaces } from "@shared/types";
import Flex from "common/Boxes/Flex.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { useIsSubUser } from "@shared/utils/hooks/misc.ts";
import { NoAccountPlacement } from "analytics/accountSetup";
import useNoExchangeConnectedActions from "features/configuration/common/useNoExchangeConnectedActions";

interface NoExchangeConnectedModalProps {
  onClose: () => void;
  action?: string;
  open?: boolean;
  redirectOnClick?: boolean;
  /** Where it showed, for analytics. */
  placement?: NoAccountPlacement;
}

const NoExchangeConnectedModal = ({
  onClose,
  action,
  open = true,
  redirectOnClick = true,
  placement = "other",
}: NoExchangeConnectedModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t: tDashboard } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const history = useHistory();
  const isSubUser = useIsSubUser();
  const {
    displayData,
    trackViewed,
    trackChoice,
    onCreateClick,
    onSwitchWorkspaceCreateClick,
    whopModal,
  } = useNoExchangeConnectedActions({
    placement,
    action,
    redirectOnClick,
    onClose,
  });

  useEffect(() => {
    if (open) trackViewed();
  }, [open]);

  const getModalContent = () => {
    if (isSubUser) {
      return (
        <EmptyView
          iconKey={EmptyStatesType.Connect}
          iconWidth={200}
          title={tDashboard("modals.sub_user_no_clients_title")}
          titleStyle={{ fontSize: typography.fontSizes.XL }}
          description={tDashboard("modals.sub_user_no_clients_content")}
          style={{ padding: 0 }}
        />
      );
    }
    return (
      <>
        <EmptyView
          iconKey={EmptyStatesType.Connect}
          iconWidth={200}
          title={displayData.title}
          titleStyle={{ fontSize: typography.fontSizes.XL }}
          description={displayData.description}
          buttonText={displayData.buttonText}
          buttonStyle={{
            width: "100%",
            height: 40,
            fontSize: typography.fontSizes.L,
          }}
          buttonOnClick={() => onCreateClick()}
          style={{ padding: 0 }}
        />
        <Flex $justifyContent="center">
          <Button
            type="link"
            style={{ marginTop: 8 }}
            onClick={onSwitchWorkspaceCreateClick}
          >
            {displayData.isDemo
              ? t("no_exchange_connected.secondary_button_demo")
              : t("no_exchange_connected.secondary_button")}
          </Button>
        </Flex>
      </>
    );
  };

  const onCancel = () => {
    trackChoice("dismissed");
    onClose();
    if (isSubUser) {
      history.push(SECURITY_SETTINGS_PATH);
    }
  };

  return (
    <>
      {whopModal}
      <Modal
        onCancel={onCancel}
        open={open}
        width={375}
        centered={true}
        footer={false}
        styles={{
          body: {
            justifyContent: "center",
          },
        }}
        destroyOnHidden={true}
      >
        {getModalContent()}
      </Modal>
    </>
  );
};

export default NoExchangeConnectedModal;
